import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { getApiOrigin } from "../../utils/api";

// Template310 (Edimburgo, copia de 110): Menú/Plan de viaje de Londres
// Adaptado del componente Page2.jsx de guia-londres para usar las props del sistema guia
const Template310 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onNavigateToPage,
	pages = [],
	prebuiltGuideId,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const pageDataEarly = page.data || {};
	// Mapeo de días a templateId (para encontrar la página correspondiente)
	const diasTemplateMapping = pageDataEarly.diasTemplateMapping || [
		111, 119, 118, 121,
	]; // Tower Bridge, Westminster, Notting Hill, Camden

	// Mapeo de extras a templateId
	const extrasTemplateMapping = pageDataEarly.extrasTemplateMapping || [
		124, 125, 126, 127, 128,
	]; // Tours, Metro, Tiendas, Aeropuertos, Consejos

	// Función para encontrar una página por templateId
	const findPageByTemplateId = (templateId) => {
		return pages.find(
			(p) => p.templateId === templateId || p.data?.templateId === templateId,
		);
	};

	const addAuthToken = (url) => {
		if (!url) return url;
		const t = localStorage.getItem("token");
		if (!t) return url;
		try {
			const u = new URL(url, window.location.origin);
			u.searchParams.set("token", t);
			return u.toString();
		} catch {
			return url;
		}
	};

	const resolveImage = (path) => {
		if (!path || typeof path !== "string") return path;
		if (path.startsWith("data:")) return path;
		if (path.startsWith("http")) return addAuthToken(path);
		if (prebuiltGuideId && path.startsWith("/images")) {
			const p = path.startsWith("/") ? path.slice(1) : path;
			return addAuthToken(
				`${getApiOrigin()}/api/prebuilt-guides/${prebuiltGuideId}/assets/${p}`,
			);
		}
		return path;
	};

	// Función para obtener el pageId de una página
	const getPageId = (page) => {
		return page?.id || page?.data?.id;
	};

	const pageData = page.data || {};
	const diasRaw = pageData.dias || [];
	const extrasRaw = pageData.extras || [];

	// Enriquecer días con pageId basado en templateId
	const dias = diasRaw.map((dia, index) => {
		const templateId = diasTemplateMapping[index];
		const targetPage = templateId ? findPageByTemplateId(templateId) : null;
		const pageId = targetPage ? getPageId(targetPage) : null;
		return {
			...dia,
			pageId: dia.pageId || pageId,
		};
	});

	// Enriquecer extras con pageId basado en templateId
	const extras = extrasRaw
		.map((extra, index) => {
			if (extra.titulo === "Kofi" || extra.image?.includes("kofi")) {
				return null;
			}
			const templateId =
				index >= 0 && index < extrasTemplateMapping.length
					? extrasTemplateMapping[index]
					: null;
			const targetPage = templateId ? findPageByTemplateId(templateId) : null;
			const pageId = targetPage ? getPageId(targetPage) : null;
			return {
				...extra,
				pageId: extra.pageId || pageId,
			};
		})
		.filter(Boolean);

	const extrasToShow = extras;

	// Lógica especial para la guía de Londres: header #de5624
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
				? page.data.order
				: -1;
	const isLondresGuide = headerBackgroundColor === "#de5624";
	let actualHeaderColor = headerBackgroundColor || "#de5624";

	const headerText = page.texts?.[0]?.content;
	const logoImage = resolveImage(
		page.canvasImages?.find((img) => img.id === "logo")?.imageUrl,
	);

	// Función para navegar
	const handleNavigate = (pageId) => {
		if (onNavigateToPage && pageId) {
			onNavigateToPage(pageId);
		}
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px",
					fontWeight: 600,
					position: "sticky",
					top: 0,
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				}}
			>
				{headerText}
				<Box
					component="img"
					src={logoImage}
					alt="logo"
					sx={{
						width: isSmallScreen ? 66 : 80,
						position: "absolute",
						right: 2,
						top: 18,
						zIndex: 999999,
					}}
				/>
			</Box>

			{/* CONTENIDO */}
			<Box data-block-id="text-1" sx={{ padding: isSmallScreen ? "10px 10px" : "18px 16px" }}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: isSmallScreen ? "10px" : "18px",
						flexWrap: "nowrap",
					}}
				>
					{dias.map((dia, index) => (
						<Box
							key={index}
							onClick={() => {
								if (dia.pageId) {
									handleNavigate(dia.pageId);
								}
							}}
							sx={{
								borderRadius: "12px",
								padding: 0,
								display: "flex",
								alignItems: "center",
								gap: isSmallScreen ? "5px" : "10px",
								position: "relative",
								minHeight: 30,
								width: "auto",
								cursor: dia.pageId ? "pointer" : "default",
								transition: "transform 0.3s ease",
								marginLeft: "20px",
								"&:hover": dia.pageId
									? {
											transform: "translateY(-5px)",
											"& .box": {
												boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
											},
										}
									: {},
							}}
						>
							{dia.image && (
								<Box
									component="img"
									src={dia.image}
									alt=""
									sx={{
										width: 45,
										zIndex: 100,
										position: "absolute",
										left: -20,
									}}
								/>
							)}
							<Box
								className="box"
								sx={{
									backgroundColor: "#fff",
									borderRadius: "12px",
									padding: "17px 16px",
									display: "flex",
									alignItems: "center",
									gap: "10px",
									position: "relative",
									minHeight: 30,
									width: "100%",
									marginLeft: 0,
									textAlign: "left",
									height: "auto",
									flex: 1,
									transition: "box-shadow 0.3s ease",
									paddingLeft: "16px",
								}}
							>
								<Box
									sx={{
										fontSize: "13px !important",
										fontWeight: 600,
										color: "#333",
										marginLeft: "20px",
										fontFamily: `${
											fontFamily || "'Poppins', sans-serif"
										} !important`,
										whiteSpace: "normal",
									}}
								>
									Día {index + 1}:{" "}
									{isSmallScreen && dia.tituloMovil
										? dia.tituloMovil.replace(/\n/g, "\n")
										: dia.titulo}
								</Box>
							</Box>
						</Box>
					))}
				</Box>

				{/* SEPARADOR */}
				<Box
					component="h3"
					sx={{
						fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
						fontSize: 12,
						fontWeight: 700,
						margin: 0,
						textTransform: "uppercase",
						marginTop: 2,
						marginBottom: 1,
					}}
				>
					Información útil
				</Box>

				{/* INFORMACIÓN ÚTIL - móvil: mismo estilo que días (como Template183) */}
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "18px",
						flexWrap: "nowrap",
						paddingBottom: "2px",
						overflowY: "visible",
					}}
				>
					{extrasToShow.map((extra, index) => (
						<Box
							key={index}
							onClick={() => {
								if (extra.pageId) {
									handleNavigate(extra.pageId);
								}
							}}
							sx={{
								borderRadius: "12px",
								padding: 0,
								display: "flex",
								alignItems: "center",
								gap: "10px",
								position: "relative",
								minHeight: 30,
								width: "auto",
								cursor: extra.pageId ? "pointer" : "default",
								transition: "transform 0.3s ease",
								marginLeft: "20px",
								"&:hover": extra.pageId
									? {
											transform: "translateY(-5px)",
											"& .box": {
												boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
											},
										}
									: {},
							}}
						>
							{extra.image && (
								<Box
									component="img"
									src={extra.image}
									alt=""
									sx={{
										width: 45,
										zIndex: 100,
										position: "absolute",
										left: -20,
									}}
								/>
							)}
							<Box
								className="box"
								sx={{
									backgroundColor: "#fff",
									borderRadius: "12px",
									padding: "17px 16px",
									display: "flex",
									alignItems: "center",
									gap: "10px",
									position: "relative",
									minHeight: 30,
									width: "100%",
									marginLeft: 0,
									textAlign: "left",
									height: "auto",
									flex: 1,
									transition: "box-shadow 0.3s ease",
									paddingLeft: "16px",
								}}
							>
								<Box
									sx={{
										fontSize: "13px !important",
										fontWeight: 600,
										color: "#333",
										marginLeft: "20px",
										fontFamily: `${
											fontFamily || "'Poppins', sans-serif"
										} !important`,
									}}
								>
									{extra.titulo}
								</Box>
							</Box>
						</Box>
					))}
				</Box>
			</Box>
		</Box>
	);
};

Template310.thumbnail = "/images/template-1.png";

export default Template310;
